import { createContext, useCallback, useContext, useState } from 'react';

/** `denied`: a pessoa (ou o sistema) recusou a permissão — o único erro que ela
 *  mesma resolve, nos ajustes. Os demais (timeout, sem GPS) ficam em `error`. */
export type BrowserPositionState = 'idle' | 'loading' | 'error' | 'denied';

export interface BrowserPosition {
  readonly latitude: number;
  readonly longitude: number;
}

/** Dez segundos de espera e um minuto de cache: o clique em "perto de mim"
 *  quer a posição de agora, não uma leitura fina — e uma segunda tentativa
 *  logo depois não paga o GPS de novo. */
const OPTIONS: PositionOptions = { timeout: 10_000, maximumAge: 60_000 };

/** O nosso próprio limite, um pouco depois do `timeout`: o navegador pode nunca chamar
 *  nenhum dos dois callbacks (o Chrome do macOS, depois da primeira leitura, deixa o
 *  pedido pendurado sem respeitar o `timeout`), e quem espera ficaria preso em "buscando". */
export const POSITION_GIVE_UP_MS = 12_000;

/** A leitura com o motivo da falha: `denied` separado do resto (#4513, I6). */
export type BrowserPositionResult =
  | { readonly kind: 'position'; readonly position: BrowserPosition }
  | { readonly kind: 'denied' }
  | { readonly kind: 'unavailable' };

/** `GeolocationPositionError.PERMISSION_DENIED`. */
const PERMISSION_DENIED = 1;

class PositionDeniedError extends Error {}

function readPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    const geolocation = navigator.geolocation as Geolocation | undefined;
    if (!geolocation) {
      reject(new Error('geolocation unavailable'));
      return;
    }
    const giveUp = setTimeout(() => {
      reject(new Error('geolocation did not answer'));
    }, POSITION_GIVE_UP_MS);
    geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(giveUp);
        resolve(position);
      },
      (error) => {
        clearTimeout(giveUp);
        reject(
          error.code === PERMISSION_DENIED
            ? new PositionDeniedError(error.message)
            : new Error(error.message),
        );
      },
      OPTIONS,
    );
  });
}

/** Quem lê a coordenada, com o motivo da falha (`denied` separado do resto). */
export type PositionReader = () => Promise<BrowserPositionResult>;

/** O leitor padrão: `navigator.geolocation`. */
export const readBrowserPosition: PositionReader = async () => {
  try {
    const { coords } = await readPosition();
    return {
      kind: 'position',
      position: { latitude: coords.latitude, longitude: coords.longitude },
    };
  } catch (error) {
    return { kind: error instanceof PositionDeniedError ? 'denied' : 'unavailable' };
  }
};

const PositionReaderContext = createContext<PositionReader>(readBrowserPosition);

/**
 * Troca quem lê a posição para a árvore abaixo — o host que tem uma fonte melhor que o
 * navegador (o app nativo, com a permissão dele) entra aqui, sem as verticais saberem.
 * Sem o provider, vale `readBrowserPosition`.
 */
export const PositionReaderProvider = PositionReaderContext.Provider;

/**
 * A posição sob demanda (do navegador, ou de quem o `PositionReaderProvider` puser no
 * lugar) — o par da `MyLocationRow`: o host chama
 * `request()` no clique, troca o par lat/lng por um lugar real (o endpoint de
 * lugares por coordenada da vertical) e faz o pick como de qualquer outra
 * linha. O hook não sabe de lugar nenhum: só lê a coordenada.
 *
 * Timeout/ausência (e o navegador que não responde nunca) caem em `state:
 * 'error'`; a permissão recusada, em `'denied'` — a dica diz onde liberar. Os
 * dois devolvem `null` em `request()`: o campo segue usável por texto. Quem
 * precisa do motivo na hora usa `read()`. O estado é do último
 * pedido, então um novo clique limpa o erro. O que vem DEPOIS da leitura
 * (geocode que não acha, BFF fora) é do host, que pode devolver o mesmo
 * `'error'` à linha pelo próprio estado.
 */
export function useBrowserPosition(): {
  state: BrowserPositionState;
  request: () => Promise<BrowserPosition | null>;
  read: () => Promise<BrowserPositionResult>;
} {
  const readSource = useContext(PositionReaderContext);
  const [state, setState] = useState<BrowserPositionState>('idle');
  const read = useCallback(async (): Promise<BrowserPositionResult> => {
    setState('loading');
    const result = await readSource();
    setState(result.kind === 'position' ? 'idle' : result.kind === 'denied' ? 'denied' : 'error');
    return result;
  }, [readSource]);
  const request = useCallback(async (): Promise<BrowserPosition | null> => {
    const result = await read();
    return result.kind === 'position' ? result.position : null;
  }, [read]);
  return { state, request, read };
}
